import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Modernized Checkbox using application semantic design tokens.
 * Supports checked, unchecked, indeterminate, hover, focus, and disabled states.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, style, ...props }, ref) => {
  const isIndeterminate = props.checked === "indeterminate";

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "peer shrink-0 grid place-items-center box-border cursor-pointer transition-colors duration-100 ease-in-out",
        "h-[14px] w-[14px] rounded-[4px] border border-[var(--checkbox-border)] bg-[var(--checkbox-background)]",
        "hover:bg-[var(--checkbox-hover-background)] hover:border-[var(--checkbox-hover-border)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--checkbox-focus-ring)] focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:bg-[var(--checkbox-disabled-background)] disabled:border-[var(--checkbox-disabled-border)]",
        "data-[state=checked]:bg-[var(--checkbox-checked-background)] data-[state=checked]:border-[var(--checkbox-checked-border)] data-[state=checked]:text-[var(--checkbox-checked-foreground)]",
        "data-[state=indeterminate]:bg-[var(--checkbox-checked-background)] data-[state=indeterminate]:border-[var(--checkbox-checked-border)]",
        className
      )}
      style={style}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className="grid place-items-center"
      >
        {isIndeterminate ? (
          <span className="block h-1.5 w-1.5 rounded-[1px] bg-[var(--checkbox-checked-foreground)] peer-disabled:bg-[var(--checkbox-disabled-foreground)]" />
        ) : (
          <Check
            className="h-2.5 w-2.5 text-[var(--checkbox-checked-foreground)] peer-disabled:text-[var(--checkbox-disabled-foreground)]"
            strokeWidth={3}
          />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
